import type { ContextsAccountWorkspace } from '../contexts/account/contract'
import { contextsAccount } from '../contexts/account'
import { deleteDB, openDB } from 'idb'
import { activeWorkspace, workspaceControlKey, workspaceDatabaseName, workspaceLockName, type WorkspaceScope } from './workspace'
import { APP_DATABASE_NAME } from '../utils/storageNames'
import { getNativePlatform } from '../utils/nativeRuntime'
import { NativeIntentInbox } from '../native/nativeIntentInbox'
import { deriveNativeAccountWorkspaceBinding } from '../native/nativeIntentProtocol'

const removalKey = (scope: WorkspaceScope) => `${workspaceControlKey(scope)}:remove`

/** Only called by the final, explicit removal action. Persist before signing out. */
export function authorizeWorkspaceRemoval(): void {
  const workspace = activeWorkspace()
  if (!workspace?.owner) throw new Error('There is no account offline copy to remove.')
  if (!navigator.locks) throw new Error('This browser cannot safely coordinate removal across tabs. Keep the offline copy and use a browser with Web Locks support to remove it.')
  localStorage.setItem(removalKey(workspace.scope), JSON.stringify({ databaseName: workspace.databaseName, owner: workspace.owner }))
}

/** Called only after the blocking account-entry UI's explicit destructive confirmation. */
export function authorizeRetainedWorkspaceRemoval(target: ContextsAccountWorkspace): void {
  if (!navigator.locks) throw new Error('This browser cannot safely coordinate removal across tabs.')
  if (!target.owner || ![APP_DATABASE_NAME, workspaceDatabaseName(target.scope,target.owner)].includes(target.databaseName)) throw new Error('Invalid offline copy target.')
  localStorage.setItem(removalKey(target.scope), JSON.stringify({ databaseName: target.databaseName, owner: target.owner }))
}

export async function finishPendingWorkspaceRemoval(scope: WorkspaceScope): Promise<void> {
  const key = removalKey(scope)
  const raw = localStorage.getItem(key)
  if (!raw) return
  const record = JSON.parse(raw) as { databaseName: string; owner: string }
  if (!record.owner || ![APP_DATABASE_NAME, workspaceDatabaseName(scope, record.owner)].includes(record.databaseName)) throw new Error('Invalid pending offline removal.')
  if (!navigator.locks) throw new Error('Offline removal requires Web Locks support.')
  if (getNativePlatform(window) === 'android') {
    if (!NativeIntentInbox.removeAccountOfflineContent) throw new Error('Update the Android app before removing offline content.')
    await NativeIntentInbox.removeAccountOfflineContent({
      workspaceBinding: await deriveNativeAccountWorkspaceBinding('supabase-google', scope.environment, record.owner), confirmed: true,
    })
  }
  await navigator.locks.request(workspaceLockName(record.databaseName), { mode: 'exclusive' }, async () => {
    let missing = false
    const database = await openDB(record.databaseName, undefined, { upgrade(_db, version, _next, tx) {
      if (version === 0) { missing = true; void tx.done.catch(() => undefined); tx.abort() }
    } }).catch(error => { if (!missing) throw error; return null })
    if (database) {
      try {
        if (!database.objectStoreNames.contains('meta')
          || (await database.get('meta', 'sync-owner'))?.value !== record.owner
          || (await database.get('meta', 'sync-environment'))?.value !== scope.environment) {
          throw new Error('Offline removal stopped because ownership could not be verified.')
        }
      } finally { database.close() }
    }
    await new Promise<void>((resolve, reject) => {
    void deleteDB(record.databaseName, { blocked() {
      reject(new Error('Removal incomplete. Close other miniMany tabs, then retry.'))
    } }).then(resolve, reject)
    })
    await contextsAccount.removeOfflineContent({ ...record, scope })
  })
  localStorage.removeItem(`minimany:note-export-attention:v1:${record.owner}`)
  localStorage.removeItem(key)
}
